import { useState, useEffect } from "react";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { AlertCircle, Loader2, Trash2 } from "lucide-react";

interface DeleteAccountDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function DeleteAccountDialog({ open, onOpenChange }: DeleteAccountDialogProps) {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [understands, setUnderstands] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [password, setPassword] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);
  const [stats, setStats] = useState({
    studyPlans: 0,
    quizSessions: 0,
    achievements: 0,
  });

  useEffect(() => {
    if (open && user) {
      loadStats();
      setStep(1);
      setUnderstands(false);
      setConfirmText("");
      setPassword("");
    }
  }, [open, user]);

  const loadStats = async () => {
    if (!user) return;

    const [plansRes, sessionsRes, achievementsRes] = await Promise.all([
      supabase.from("study_plans").select("id", { count: "exact" }).eq("user_id", user.id),
      supabase.from("quiz_sessions").select("id", { count: "exact" }).eq("user_id", user.id),
      supabase.from("user_achievements").select("id", { count: "exact" }).eq("user_id", user.id),
    ]);

    setStats({
      studyPlans: plansRes.count || 0,
      quizSessions: sessionsRes.count || 0,
      achievements: achievementsRes.count || 0,
    });
  };

  const handleDelete = async () => {
    if (!user || confirmText !== "DELETE" || !password) return;

    setIsDeleting(true);
    try {
      const { data, error } = await supabase.functions.invoke("delete-user-account", {
        body: {
          password: password,
          confirmText: confirmText,
        },
      });

      if (error) throw error;

      toast.success("Account deleted successfully");
      await signOut();
      navigate("/");
      onOpenChange(false);
    } catch (error: any) {
      console.error("Error deleting account:", error);
      toast.error(error.message || "Failed to delete account");
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="max-w-2xl">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2 text-destructive">
            <AlertCircle className="h-5 w-5" />
            Are you absolutely sure?
          </AlertDialogTitle>
          <AlertDialogDescription className="space-y-4">
            {step === 1 && (
              <>
                <p>This will permanently delete your account and remove all data.</p>

                <div className="bg-muted p-4 rounded-lg space-y-2">
                  <h4 className="font-medium">What will be deleted:</h4>
                  <ul className="text-sm space-y-1">
                    <li>✗ {stats.studyPlans} study plans</li>
                    <li>✗ {stats.quizSessions} quiz sessions</li>
                    <li>✗ {stats.achievements} achievements</li>
                    <li>✗ All progress and analytics</li>
                  </ul>
                </div>

                <div className="flex items-center gap-2">
                  <Checkbox
                    checked={understands}
                    onCheckedChange={(checked) => setUnderstands(checked as boolean)}
                    id="understand"
                  />
                  <Label htmlFor="understand" className="text-sm">
                    I understand this action cannot be undone
                  </Label>
                </div>
              </>
            )}

            {step === 2 && (
              <>
                <p className="font-medium">Type "DELETE" to confirm:</p>
                <Input
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  placeholder="Type DELETE"
                  className="font-mono"
                />

                <p className="text-sm">Enter your password to proceed:</p>
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Password"
                />
              </>
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>

        <AlertDialogFooter>
          <AlertDialogCancel onClick={() => onOpenChange(false)}>Cancel</AlertDialogCancel>
          {step === 1 ? (
            <Button
              variant="destructive"
              onClick={() => setStep(2)}
              disabled={!understands}
            >
              Continue
            </Button>
          ) : (
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={confirmText !== "DELETE" || !password || isDeleting}
            >
              {isDeleting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete Account
                </>
              )}
            </Button>
          )}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
